// =============================================================================
// components/ui/useReducedMotionTransparency.ts — the ONE accessibility hook the
// new dark pattern primitives use to honor the two OS toggles the design-system
// motion + material rules care about (apple-design SKILL §14):
//
//   • prefers-reduced-motion  → replace spring slides/scale with a plain opacity
//     cross-fade (no vestibular motion), drop overshoot.
//   • prefers-reduced-transparency → make glass frostier/near-solid: raise the
//     surface fill opacity and skip decorative dim scrims.
//
// This is READ-ONLY state: it never touches GlassSurface (the metro-critical
// dedup + its existing press spring stay untouched) — each primitive decides
// how to degrade using these two booleans. Mirrors the existing convention in
// components/home/GetStartedCard.tsx (isReduceMotionEnabled + subscription),
// extended with the transparency signal.
// =============================================================================
import { useEffect, useState } from 'react';
import { AccessibilityInfo } from 'react-native';

export interface ReducedMotionTransparency {
  /** OS "Reduce Motion" — cross-fade instead of spring, no overshoot. */
  reduceMotion: boolean;
  /** OS "Reduce Transparency" — frost/solidify glass, drop decorative scrims. */
  reduceTransparency: boolean;
}

export function useReducedMotionTransparency(): ReducedMotionTransparency {
  const [reduceMotion, setReduceMotion] = useState(false);
  const [reduceTransparency, setReduceTransparency] = useState(false);

  useEffect(() => {
    let active = true;

    AccessibilityInfo.isReduceMotionEnabled()
      .then((v) => { if (active) setReduceMotion(!!v); })
      .catch(() => {});

    // isReduceTransparencyEnabled exists on iOS; guard for platforms/versions
    // that don't expose it so this stays additive + crash-free.
    const anyA11y = AccessibilityInfo as unknown as {
      isReduceTransparencyEnabled?: () => Promise<boolean>;
    };
    anyA11y.isReduceTransparencyEnabled?.()
      .then((v) => { if (active) setReduceTransparency(!!v); })
      .catch(() => {});

    const motionSub = AccessibilityInfo.addEventListener(
      'reduceMotionChanged',
      (v) => setReduceMotion(!!v),
    );
    // 'reduceTransparencyChanged' is iOS-only; the typed event map may not list
    // it on every RN version, so subscribe through a loose cast.
    const transSub = (AccessibilityInfo.addEventListener as unknown as (
      event: string,
      handler: (v: boolean) => void,
    ) => { remove: () => void })(
      'reduceTransparencyChanged',
      (v) => setReduceTransparency(!!v),
    );

    return () => {
      active = false;
      motionSub?.remove?.();
      transSub?.remove?.();
    };
  }, []);

  return { reduceMotion, reduceTransparency };
}

export default useReducedMotionTransparency;
